import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowDown, ArrowUpRight, ArrowRight } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import Counter from "../components/Counter";
import Reveal from "../components/Reveal";
import ServiceCard from "../components/ServiceCard";
import MagneticButton from "../components/MagneticButton";
import { TESTIMONIALS } from "../i18n/translations";

const HERO_BG = "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1800&q=85";

const Home = () => {
  const { lang, t } = useLang();
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [tIndex, setTIndex] = useState(0);
  const testimonials = TESTIMONIALS[lang];

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
    api.get("/portfolio").then(({ data }) => setPortfolio(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const id = setInterval(() => setTIndex((i) => (i + 1) % testimonials.length), 5500);
    return () => clearInterval(id);
  }, [testimonials.length]);

  const heroWords = [t.home.heroLine1, t.home.heroLine2, t.home.heroLine3];
  const featured = services.slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        style={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          padding: "120px clamp(24px, 6vw, 96px) 80px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute", inset: 0,
            background: `url(${HERO_BG}) center/cover no-repeat`,
            opacity: 0.45, zIndex: 0,
          }}
        />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(10,10,10,0.5) 0%, rgba(10,10,10,0.85) 70%, var(--rich-black) 100%)", zIndex: 1 }} />
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 30% 50%, rgba(139,32,53,0.35), transparent 60%)", zIndex: 1 }} />

        <div style={{ position: "relative", zIndex: 2, maxWidth: 1400, width: "100%" }}>
          <div className="font-mono line-deco" style={{ fontSize: "0.75rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 32 }}>
            Vakar Agency — Est. 2019
          </div>
          <h1 className="font-display" style={{ fontSize: "clamp(3rem, 9vw, 8rem)", lineHeight: 0.95, fontWeight: 300, letterSpacing: "-0.02em", color: "var(--warm-off-white)", marginBottom: 32 }}>
            {heroWords.map((w, i) => (
              <span key={`${lang}-${i}`} style={{ display: "block", overflow: "hidden" }}>
                <span className="hero-word" style={{ animationDelay: `${0.3 + i * 0.18}s`, color: i === 1 ? "var(--gold-light)" : "var(--warm-off-white)", fontStyle: i === 1 ? "italic" : "normal" }}>
                  {w}
                </span>
              </span>
            ))}
          </h1>
          <p className="font-body" style={{ fontSize: "clamp(1rem, 1.4vw, 1.25rem)", color: "rgba(248,244,240,0.7)", maxWidth: 560, lineHeight: 1.6, marginBottom: 48, opacity: 0, animation: "word-reveal 1s var(--ease-cinema) 1s forwards" }}>
            {t.home.heroSubtitle}
          </p>
          <div className="flex flex-wrap items-center" style={{ gap: 24, opacity: 0, animation: "word-reveal 1s var(--ease-cinema) 1.2s forwards" }}>
            <Link to="/contact" data-testid="hero-cta-primary" style={{ textDecoration: "none" }}>
              <MagneticButton className="font-mono" style={{ padding: "18px 36px", background: "var(--bordeaux)", color: "var(--warm-off-white)", border: "none", textTransform: "uppercase", letterSpacing: "0.2em", fontSize: "0.75rem", cursor: "pointer" }}>
                {t.nav.getInTouch} <ArrowUpRight size={16} style={{ marginLeft: 8 }} />
              </MagneticButton>
            </Link>
            <Link to="/portfolio" data-testid="hero-cta-secondary" className="link-underline font-mono" style={{ color: "var(--warm-off-white)", textDecoration: "none", textTransform: "uppercase", letterSpacing: "0.2em", fontSize: "0.75rem", display: "inline-flex", alignItems: "center", gap: 8 }}>
              {t.home.recentWorkCta} <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        <div className="scroll-indicator" style={{ position: "absolute", bottom: 36, left: "50%", transform: "translateX(-50%)", color: "rgba(248,244,240,0.55)", zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <span className="font-mono" style={{ fontSize: "0.7rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>{t.home.scroll}</span>
          <ArrowDown size={18} />
        </div>
      </section>

      {/* TICKER */}
      <div className="service-ticker" data-testid="service-ticker">
        <Marquee gradient={false} speed={32}>
          {services.length > 0 && [...services, ...services].map((s, i) => (
            <span key={i} className="font-display" style={{ fontSize: "clamp(1.5rem, 3vw, 2.5rem)", fontWeight: 300, color: i % 2 === 0 ? "var(--warm-off-white)" : "var(--gold)", margin: "0 48px", fontStyle: i % 3 === 0 ? "italic" : "normal" }}>
              {lang === "tr" ? s.title_tr : s.title_en} <span style={{ color: "var(--bordeaux-light)", marginLeft: 48 }}>✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* STATS */}
      <section className="section-pad" data-testid="stats-section">
        <div className="font-mono line-deco" style={{ fontSize: "0.75rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 48 }}>
          {t.home.statsLabel}
        </div>
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 48 }}>
          {[
            { n: 150, suffix: "+", l: t.home.statProjects },
            { n: 98, suffix: "%", l: t.home.statSatisfaction },
            { n: 50, suffix: "+", l: t.home.statClients },
            { n: 6, suffix: "+", l: t.home.statYears },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 100}>
              <div style={{ borderLeft: "1px solid rgba(201,151,58,0.4)", paddingLeft: 24 }}>
                <div className="font-display" style={{ fontSize: "clamp(3rem, 7vw, 6rem)", fontWeight: 300, color: "var(--warm-off-white)", lineHeight: 1 }}>
                  <Counter to={s.n} suffix={s.suffix} />
                </div>
                <div className="font-mono" style={{ fontSize: "0.7rem", letterSpacing: "0.2em", color: "rgba(248,244,240,0.55)", textTransform: "uppercase", marginTop: 16 }}>{s.l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHAT WE DO TEASER */}
      <section className="section-pad" style={{ background: "linear-gradient(180deg, var(--rich-black) 0%, var(--rich-black-light) 100%)" }} data-testid="what-we-do">
        <div className="flex flex-wrap items-end justify-between" style={{ marginBottom: 64, gap: 32 }}>
          <div style={{ maxWidth: 720 }}>
            <div className="font-mono line-deco" style={{ fontSize: "0.75rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 24 }}>
              {t.home.whatWeDoLabel}
            </div>
            <h2 className="font-display" style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)", fontWeight: 300, lineHeight: 1.05, color: "var(--warm-off-white)" }}>
              {t.home.whatWeDoTitle}
            </h2>
            <p className="font-body" style={{ color: "rgba(248,244,240,0.65)", lineHeight: 1.7, marginTop: 24, fontSize: "1.05rem", maxWidth: 560 }}>
              {t.home.whatWeDoIntro}
            </p>
          </div>
          <Link to="/services" data-testid="home-services-link" className="link-underline font-mono" style={{ color: "var(--gold)", textDecoration: "none", textTransform: "uppercase", letterSpacing: "0.2em", fontSize: "0.75rem", display: "inline-flex", alignItems: "center", gap: 8 }}>
            {t.services.title} <ArrowUpRight size={16} />
          </Link>
        </div>
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
          {featured.map((s, i) => (
            <Reveal key={s.id} delay={i * 100}>
              <Link to="/services" style={{ textDecoration: "none" }}>
                <ServiceCard service={s} index={i} lang={lang} />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* RECENT WORK — horizontal scroll */}
      <section className="section-pad" data-testid="recent-work">
        <div className="flex flex-wrap items-end justify-between" style={{ marginBottom: 48, gap: 32 }}>
          <div>
            <div className="font-mono line-deco" style={{ fontSize: "0.75rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 24 }}>
              {t.home.recentWorkLabel}
            </div>
            <h2 className="font-display" style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)", fontWeight: 300, lineHeight: 1.05, color: "var(--warm-off-white)" }}>
              {t.home.recentWorkTitle}
            </h2>
          </div>
          <Link to="/portfolio" className="link-underline font-mono" style={{ color: "var(--gold)", textDecoration: "none", textTransform: "uppercase", letterSpacing: "0.2em", fontSize: "0.75rem", display: "inline-flex", alignItems: "center", gap: 8 }}>
            {t.home.recentWorkCta} <ArrowUpRight size={16} />
          </Link>
        </div>
        <div className="h-scroll" data-testid="home-portfolio-scroll">
          {portfolio.slice(0, 8).map((p) => (
            <div key={p.id} style={{ width: "min(70vw, 420px)" }}>
              <div style={{ position: "relative", aspectRatio: "4/5", overflow: "hidden", background: "var(--rich-black-light)" }}>
                <img src={p.image_url} alt={lang === "tr" ? p.title_tr : p.title_en} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.8s var(--ease-cinema)", filter: "brightness(0.85)" }} onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")} onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")} />
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 40%, rgba(10,10,10,0.95) 100%)" }} />
                <div style={{ position: "absolute", bottom: 24, left: 24, right: 24 }}>
                  <span className="font-mono" style={{ fontSize: "0.7rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase" }}>{p.category} — {p.year}</span>
                  <h3 className="font-display" style={{ fontSize: "1.6rem", fontWeight: 400, color: "var(--warm-off-white)", marginTop: 8, lineHeight: 1.15 }}>{lang === "tr" ? p.title_tr : p.title_en}</h3>
                  <div className="font-mono" style={{ fontSize: "0.8rem", color: "var(--gold-light)", marginTop: 8 }}>{p.result_metric}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad" style={{ background: "linear-gradient(180deg, var(--rich-black) 0%, #170a0e 100%)" }} data-testid="testimonials">
        <div className="font-mono line-deco" style={{ fontSize: "0.75rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 48 }}>
          {t.home.testimonialsLabel}
        </div>
        <div style={{ maxWidth: 1100, position: "relative", minHeight: 260 }}>
          {testimonials.map((tm, i) => (
            <div key={i} style={{
              position: i === tIndex ? "relative" : "absolute",
              inset: 0,
              opacity: i === tIndex ? 1 : 0,
              transition: "opacity 1s var(--ease-cinema)",
              pointerEvents: i === tIndex ? "auto" : "none",
            }}>
              <p className="font-display" style={{ fontSize: "clamp(1.5rem, 3vw, 2.75rem)", fontWeight: 300, fontStyle: "italic", color: "var(--warm-off-white)", lineHeight: 1.3 }}>
                "{tm.q}"
              </p>
              <div className="font-mono" style={{ marginTop: 32, color: "var(--gold)", letterSpacing: "0.15em", fontSize: "0.85rem", textTransform: "uppercase" }}>
                — {tm.n}, <span style={{ color: "rgba(248,244,240,0.55)" }}>{tm.c}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="flex" style={{ gap: 8, marginTop: 48 }}>
          {testimonials.map((_, i) => (
            <button key={i} data-testid={`testimonial-dot-${i}`} onClick={() => setTIndex(i)} aria-label={`testimonial ${i + 1}`} style={{ width: i === tIndex ? 32 : 12, height: 2, background: i === tIndex ? "var(--gold)" : "rgba(248,244,240,0.25)", border: "none", cursor: "pointer", transition: "width 0.5s var(--ease-cinema), background 0.4s ease" }} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: "linear-gradient(135deg, var(--bordeaux-dark), var(--bordeaux))", padding: "clamp(80px, 12vw, 160px) clamp(24px, 6vw, 96px)", position: "relative", overflow: "hidden" }} data-testid="home-cta">
        <Reveal>
          <h2 className="font-display" style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)", fontWeight: 300, color: "var(--warm-off-white)", lineHeight: 1.05, maxWidth: 1100 }}>
            {t.home.ctaTitle}
          </h2>
          <Link to="/contact" style={{ textDecoration: "none", display: "inline-block", marginTop: 48 }}>
            <MagneticButton data-testid="home-cta-button" className="font-mono" style={{ padding: "20px 44px", background: "var(--warm-off-white)", color: "var(--bordeaux-dark)", border: "none", textTransform: "uppercase", letterSpacing: "0.2em", fontSize: "0.75rem", cursor: "pointer" }}>
              {t.home.ctaButton} <ArrowUpRight size={16} style={{ marginLeft: 8 }} />
            </MagneticButton>
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
